import { z } from 'zod';
/**
 * Cookieloser Besucher-Funnel und Chat-Auswertung für das Admin-Portal.
 *
 * **Warum das ohne Einwilligung geht:** Auf dem Endgerät wird nichts gespeichert und nichts
 * ausgelesen; der Server zählt nur Tageswerte je Seitenpfad und Herkunftskategorie. Es gibt
 * keine Kennung, die einen Besucher über einen Aufruf hinaus wiedererkennbar macht — weder IP
 * noch User-Agent landen in der Datenbank. Sobald hier ein Identifikator dazukommt (Cookie,
 * Fingerprint, Session-ID im Browser), ist die Grundlage für „keine Einwilligung nötig" weg.
 *
 * **Die `gclid` selbst wird nie übertragen.** Der Client reduziert sie auf die Kategorie `ads`.
 */
/** Herkunft eines Aufrufs. `internal` ist ein Folgeaufruf auf derselben Seite, kein Einstieg. */
export const InsightSourceSchema = z.enum(['ads', 'search', 'social', 'referral', 'direct', 'internal']);
export const InsightEventTypeSchema = z.enum(['pageview', 'phone_click', 'contact_open']);
/** Deutsche Beschriftungen der Herkunft — an einer Stelle für Admin-App und Auswertung. */
export const INSIGHT_SOURCE_LABELS = {
    ads: 'Anzeigen',
    search: 'Suchmaschinen',
    social: 'Soziale Netzwerke',
    referral: 'Andere Websites',
    direct: 'Direkt',
    internal: 'Folgeaufruf',
};
/** Der Pfad, unter dem alles gezählt wird, was sich nicht sauber normalisieren lässt. */
export const INSIGHT_OTHER_PATH = '/andere';
/** `POST /insights/events` — öffentlich, ohne Anmeldung. Der Rumpf ist höchstens 1 KB groß. */
export const TrackInsightEventInputSchema = z.object({
    type: InsightEventTypeSchema,
    /** Roh `location.pathname`; der Server normalisiert und verwirft Unbrauchbares nach `/andere`. */
    path: z.string().min(1).max(200),
    source: InsightSourceSchema.default('direct'),
});
/** Höchstgröße des Rumpfs von `POST /insights/events` in Zeichen. */
export const INSIGHT_MAX_BODY_CHARS = 1024;
/* --------------------------------------------------------------------------------------------
 * Admin-Sicht: GET /admin/insights
 * ----------------------------------------------------------------------------------------- */
export const InsightCountersSchema = z.object({
    pageviews: z.int().min(0),
    entries: z.int().min(0),
    adEntries: z.int().min(0),
    phoneClicks: z.int().min(0),
    chatStarts: z.int().min(0),
    chatReady: z.int().min(0),
    contactOpens: z.int().min(0),
    submits: z.int().min(0),
});
export const InsightDaySchema = InsightCountersSchema.extend({ day: z.string() });
export const InsightPageSchema = InsightCountersSchema.extend({ path: z.string() });
export const InsightSourceRowSchema = z.object({
    source: InsightSourceSchema,
    entries: z.int().min(0),
    chatStarts: z.int().min(0),
    submits: z.int().min(0),
});
export const InsightsViewSchema = z.object({
    days: z.int(),
    from: z.string(),
    to: z.string(),
    totals: InsightCountersSchema,
    daily: z.array(InsightDaySchema),
    pages: z.array(InsightPageSchema),
    sources: z.array(InsightSourceRowSchema),
});
/* --------------------------------------------------------------------------------------------
 * Admin-Sicht: Chat-Gespräche
 * ----------------------------------------------------------------------------------------- */
/**
 * Wie ein Gespräch ausgegangen ist — **abgeleitet**, nicht gespeichert (der Status
 * `abandoned` wird nie gesetzt).
 */
export const ChatOutcomeSchema = z.enum(['submitted', 'ready_not_submitted', 'limit_reached', 'abandoned', 'active']);
export const CHAT_OUTCOME_LABELS = {
    submitted: 'Abgesendet',
    ready_not_submitted: 'Zusammenfassung, nicht abgesendet',
    limit_reached: 'Limit erreicht',
    abandoned: 'Abgebrochen',
    active: 'Läuft',
};
/** Ab dieser Zeit ohne Aktivität gilt ein offenes Gespräch als abgebrochen. */
export const CHAT_ABANDONED_AFTER_MINUTES = 30;
export const ChatSessionDraftSummarySchema = z.object({
    /** Art der Bewirtung — der Entwurf kennt keinen eigenen „Anlass". */
    serviceTypes: z.array(z.string()),
    eventDate: z.string().nullable(),
    city: z.string().nullable(),
    postalCode: z.string().nullable(),
    guestCount: z.int().nullable(),
});
export const AdminChatSessionListItemSchema = z.object({
    sessionId: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    turnCount: z.int(),
    outcome: ChatOutcomeSchema,
    entryPath: z.string().nullable(),
    source: InsightSourceSchema.nullable(),
    draft: ChatSessionDraftSummarySchema,
    rfqId: z.string().nullable(),
    inputTokens: z.int(),
    outputTokens: z.int(),
});
export const AdminChatSessionListSchema = z.object({
    items: z.array(AdminChatSessionListItemSchema),
    cursor: z.string().nullable(),
});
